import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useColorTheme } from "./hooks/useColorTheme";
import { DynamicStyles } from "./components/DynamicStyles";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AdminPreviewNav from "./components/AdminPreviewNav";
import Home from "./pages/Home";
import About from "./pages/About";
import QA from "./pages/QA";
import Downloads from "./pages/Downloads";
import Booking from "./pages/Booking";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import Book from "./pages/Book";
import Contact from "./pages/Contact";
import BookingSuccess from "./pages/BookingSuccess";
import AdminLogin from "./pages/Admin/AdminLogin";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminSessions from "./pages/Admin/AdminSessions";
import AdminBookings from "./pages/Admin/AdminBookings";
import AdminMessages from "./pages/Admin/AdminMessages";
import AdminBlog from "./pages/Admin/AdminBlog";
import AdminSettings from "./pages/Admin/AdminSettings";
import AdminContent from "./pages/Admin/AdminContent";
import AdminDownloads from "./pages/Admin/AdminDownloads";
import AdminSubscribers from "./pages/Admin/AdminSubscribers";
import HandoffCallback from "./pages/HandoffCallback";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Main App component
function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

// Main App Content with Color Theme
const AppContent = () => {
  const { colors } = useColorTheme(); // Apply color theme globally

  return (
    <>
      <DynamicStyles colors={colors} />
      <Routes>
            {/* Public Routes */}
            <Route
              path="/*"
              element={
                <>
                  <Header />
                  <main>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/qa" element={<QA />} />
                      <Route path="/downloads" element={<Downloads />} />
                      <Route path="/booking" element={<Booking />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:id" element={<BlogDetail />} />
                      <Route path="/book" element={<Book />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/booking/success" element={<BookingSuccess />} />
                    </Routes>
                  </main>
                  <Footer />
                </>
              }
            />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/auth/handoff" element={<HandoffCallback />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sessions"
              element={
                <ProtectedRoute>
                  <AdminSessions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bookings"
              element={
                <ProtectedRoute>
                  <AdminBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/messages"
              element={
                <ProtectedRoute>
                  <AdminMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute>
                  <AdminBlog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/content"
              element={
                <ProtectedRoute>
                  <AdminContent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/downloads"
              element={
                <ProtectedRoute>
                  <AdminDownloads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subscribers"
              element={
                <ProtectedRoute>
                  <AdminSubscribers />
                </ProtectedRoute>
              }
            />
          </Routes>
      <AdminPreviewNav />
    </>
  );
}

export default App;
